import { useCallback, useEffect, useRef } from "react";
import type { ClinicResult } from "../setup-flow-config";

interface Props {
    clinic: ClinicResult;
    onConfirm: () => Promise<void> | void;
    onReject: () => void;
}

export default function ClinicConfirmStep({ clinic, onConfirm, onReject }: Props) {
    const savingRef = useRef(false);

    const handleConfirm = useCallback(async () => {
        if (savingRef.current) return;
        savingRef.current = true;
        try {
            await onConfirm();
        } finally {
            savingRef.current = false;
        }
    }, [onConfirm]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                e.preventDefault();
                handleConfirm();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleConfirm]);

    return (
        <div className="flex w-full max-w-[520px] flex-col items-center gap-10">
            <h1 className="fraunces-soft text-4xl font-semibold leading-tight text-shadow-700">Is this you?</h1>

            {/* Clinic detail card */}
            <div className="w-full rounded-xl border border-shadow-50 bg-shadow-25 px-8 py-6">
                <p className="text-xl font-semibold text-shadow-700">{clinic.name}</p>
                <p className="mt-1.5 text-base text-shadow-500">{clinic.formattedAddress}</p>
                {clinic.phone && <p className="mt-1 text-base text-shadow-500">{clinic.phone}</p>}
                {clinic.website && (
                    <a
                        href={clinic.website.startsWith("http") ? clinic.website : `https://${clinic.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 block text-base text-max-900 hover:underline"
                    >
                        {clinic.website}
                    </a>
                )}
            </div>

            {/* Buttons */}
            <div className="flex w-full gap-4">
                <button
                    onClick={onReject}
                    className="flex-1 rounded-xl border border-shadow-100 bg-white py-3.5 text-base font-semibold text-shadow-500 hover:bg-gray-50 focused"
                >
                    Not this one
                </button>
                <button
                    onClick={handleConfirm}
                    className="flex-[2] rounded-xl bg-max-900 py-3.5 text-lg font-semibold text-white hover:bg-max-850 focused"
                >
                    Yes, that's us!
                </button>
            </div>

            <p className="text-sm text-shadow-400">
                Press{" "}
                <kbd className="rounded border border-shadow-100 bg-shadow-25 px-1.5 py-0.5 font-mono text-xs">
                    Enter ↵
                </kbd>{" "}
                to confirm
            </p>
        </div>
    );
}
